import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useUpdateTaskStatusMutation } from "@/store/rest_api";
import { daysSince } from "@/util/date";
import type { AttentionTask, NeedsAttention } from "@/model/interface/DashboardSummary";

const TodaysTasksCard: React.FC<{ needsAttention?: NeedsAttention; tasksDue: number }> = ({
  needsAttention,
  tasksDue,
}) => {
  const navigate = useNavigate();
  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const [hiddenTaskIds, setHiddenTaskIds] = useState<Set<string>>(new Set());

  const completeTask = async (task: AttentionTask) => {
    if (!task.id) {
      return;
    }
    const id = task.id;
    setHiddenTaskIds((previous) => new Set(previous).add(id));
    try {
      await updateTaskStatus({ id, status: "DONE" }).unwrap();
      toast.success("Task marked done");
    } catch {
      setHiddenTaskIds((previous) => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
      toast.error("Could not complete task");
    }
  };

  const openTask = (task: AttentionTask) => {
    if (task.id) {
      navigate(`/tasks?task=${task.id}`);
    } else {
      navigate("/tasks");
    }
  };

  const dueLabel = (task: AttentionTask): { label: string; overdue: boolean } => {
    if (task.overdue) {
      const days = daysSince(task.dueDate) ?? 0;
      return { label: `${days}d overdue`, overdue: true };
    }
    return { label: "Due today", overdue: false };
  };

  if (!needsAttention) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's tasks</h2>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </section>
    );
  }

  const tasks = [...needsAttention.overdueTasks, ...needsAttention.dueTodayTasks]
    .filter((task) => !task.id || !hiddenTaskIds.has(task.id))
    .slice(0, 6);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's tasks</h2>
        <Button variant="link" size="sm" className="h-auto px-0 text-xs" onClick={() => navigate("/tasks")}>
          View all
        </Button>
      </div>
      {tasks.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">Nothing due today.</p>
      ) : (
        <div className="flex flex-col">
          {tasks.map((task) => {
            const due = dueLabel(task);
            return (
              <div
                key={task.id ?? task.title}
                role="button"
                tabIndex={0}
                onClick={() => openTask(task)}
                className="group flex cursor-pointer items-start gap-2.5 rounded-md px-1 py-2 hover:bg-muted/50"
              >
                <Checkbox
                  className="mt-0.5"
                  aria-label="Mark done"
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={() => completeTask(task)}
                />
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{task.title}</span>
                  <p className="truncate text-xs text-muted-foreground">
                    {task.contactName ? `${task.contactName} · ` : ""}
                    <span className={due.overdue ? "text-destructive" : undefined}>{due.label}</span>
                  </p>
                </div>
              </div>
            );
          })}
          {tasksDue > tasks.length && (
            <Button
              variant="link"
              size="sm"
              className="h-auto justify-start px-1 text-xs"
              onClick={() => navigate("/tasks")}
            >
              View all {tasksDue} due
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

export default TodaysTasksCard;
